import {LOGGED_IN_USER as LOGGED_IN_USER_KEY} from "./Constants";

export const ROLES = {
  SUPER_ADMIN:     "SUPER_ADMIN",
  CLIENT_ADMIN:    "CLIENT_ADMIN",
  CLIENT_MANAGER:  "CLIENT_MANAGER",
  CLIENT_EMPLOYEE: "CLIENT_EMPLOYEE",
  MOBI_TRACK:      "MOBI_TRACK",
};

const { SUPER_ADMIN, CLIENT_ADMIN, CLIENT_MANAGER, CLIENT_EMPLOYEE, MOBI_TRACK } = ROLES;

const ALL_CLIENT_ROLES   = [CLIENT_ADMIN, CLIENT_MANAGER, CLIENT_EMPLOYEE, MOBI_TRACK];
const MANAGER_AND_ABOVE  = [CLIENT_ADMIN, CLIENT_MANAGER];
const ADMIN_ONLY         = [CLIENT_ADMIN];
const SUPER_ONLY         = [SUPER_ADMIN];
// Track Point (MOBI_TRACK) is a field-staff role that only ever sees Live Tracking — mirrors
// the Android app, where TrackingActivity is the only screen a MOBI_TRACK driver ever sees. It
// must never reach the fleet Dashboard (every device across the client, not just its own) or
// the Assets/Users/Reports management screens that assume an office-based operator.
const DASHBOARD_ROLES    = [CLIENT_ADMIN, CLIENT_MANAGER, CLIENT_EMPLOYEE];

export const PERMISSIONS = {
  // ── Sidebar / Navigation ─────────────────────────────────────────
  sidebar_dashboard:     [...DASHBOARD_ROLES, SUPER_ADMIN], // MOBI_TRACK: HIDDEN — Track Point only ever sees Live Tracking
  sidebar_assets:        [CLIENT_ADMIN, CLIENT_MANAGER], // Employee: HIDDEN (point 1 — employees work from the scoped Dashboard)
  sidebar_tracking:      ALL_CLIENT_ROLES,    // Live Tracking: all roles (employees: their assigned assets)
  sidebar_users:         ADMIN_ONLY,            // Manager: HIDDEN, Employee: HIDDEN
  sidebar_tasks:         MANAGER_AND_ABOVE,     // Tasks: Admin + Manager only (Employee HIDDEN)
  sidebar_trips:         MANAGER_AND_ABOVE,     // Trips: Admin + Manager only (Employee HIDDEN)
  sidebar_reports:       MANAGER_AND_ABOVE,     // Employee: HIDDEN
  sidebar_analytics:     MANAGER_AND_ABOVE,     // Allocation analytics (point 12): Admin + Manager
  sidebar_commandCenter: MANAGER_AND_ABOVE,     // Employee: HIDDEN
  sidebar_scheduledUnlocks: MANAGER_AND_ABOVE,  // Employee: HIDDEN (mirrors Command Center's UNLOCK gate)
  sidebar_settings:      ADMIN_ONLY,            // Manager: HIDDEN, Employee: HIDDEN
  sidebar_profile:       [...ALL_CLIENT_ROLES, SUPER_ADMIN],
  sidebar_auditLog:      [...MANAGER_AND_ABOVE, SUPER_ADMIN], // Employee: HIDDEN

  // ── Assets – sub-tabs ────────────────────────────────────────────
  assets_registerAsset:  ADMIN_ONLY,            // Register Asset tab: Admin only
  assets_allocation:     MANAGER_AND_ABOVE,     // Asset Allocation tab: Admin + Manager
  assets_passcode:       MANAGER_AND_ABOVE,     // Passcode Generation tab: Admin + Manager
  assets_viewActions:    MANAGER_AND_ABOVE,     // Edit/Suspend icons: removed from DOM for Employee

  // ── Assets – CRUD ────────────────────────────────────────────────
  assets_add:    ADMIN_ONLY,                    // POST /assets blocked for Manager/Employee
  assets_edit:   MANAGER_AND_ABOVE,
  assets_delete: ADMIN_ONLY,

  // ── Users ────────────────────────────────────────────────────────
  users_register:       ADMIN_ONLY,
  users_edit:           ADMIN_ONLY,
  users_suspend:        ADMIN_ONLY,
  users_delete:         ADMIN_ONLY,           // Soft-delete a user: Admin only (backend enforces too)
  users_resetPassword:  ADMIN_ONLY,          // Reset a user's password (point 4): Admin only
  users_enableTracking: MANAGER_AND_ABOVE,

  // ── Tasks ────────────────────────────────────────────────────────
  tasks_viewAll: MANAGER_AND_ABOVE,
  tasks_manage:  MANAGER_AND_ABOVE,
  tasks_delete:  ADMIN_ONLY,

  // ── Trips ────────────────────────────────────────────────────────
  trips_viewAll:     MANAGER_AND_ABOVE,
  trips_manage:      MANAGER_AND_ABOVE,
  trips_manageLocks: MANAGER_AND_ABOVE,
  trips_delete:      ADMIN_ONLY,

  // ── Reports ──────────────────────────────────────────────────────
  reports_view:   MANAGER_AND_ABOVE,
  reports_export: MANAGER_AND_ABOVE,

  // ── Command Center ───────────────────────────────────────────────
  commandCenter_view:      MANAGER_AND_ABOVE,
  commandCenter_send:      MANAGER_AND_ABOVE,
  commandCenter_configure: ADMIN_ONLY,           // Only Admin can configure

  // ── Scheduled Group Unlock (GPS locks) ──────────────────────────────
  scheduledUnlocks_manage: MANAGER_AND_ABOVE,   // Schedule / cancel a group unlock session

  // ── Settings tabs ────────────────────────────────────────────────
  settings_notifications:  ADMIN_ONLY,           // Settings page is Admin-only
  settings_customerList:   ADMIN_ONLY,
  settings_uploadCustomer: ADMIN_ONLY,
  settings_mapping:        ADMIN_ONLY,

  // ── Audit Log ────────────────────────────────────────────────────
  auditLog_viewClientLogs:   MANAGER_AND_ABOVE,
  auditLog_viewPlatformLogs: SUPER_ONLY,
  auditLog_export:           [...ADMIN_ONLY, SUPER_ADMIN], // Manager is read-only

  // ── Super Admin ──────────────────────────────────────────────────
  superAdmin_manageClients: SUPER_ONLY,
};

export const getRole = () => localStorage.getItem("role") || "";

export const hasPermission = (permission) => {
  const role = getRole();
  return Array.isArray(PERMISSIONS[permission]) && PERMISSIONS[permission].includes(role);
};

export const isSuperAdmin  = () => getRole() === ROLES.SUPER_ADMIN;
export const isClientAdmin = () => getRole() === ROLES.CLIENT_ADMIN;

/**
 * The one landing/fallback destination for a given role — used both right after login
 * (LandingPage's applyLoginSession) and as the redirect target when a role is denied a
 * guarded route (App.js's RoleRoute). A second, independent copy of this mapping is exactly
 * how CLIENT_EMPLOYEE previously ended up routed to /tracking instead of the scoped Dashboard:
 * only LandingPage's copy included MOBI_TRACK-only logic correctly, so the other copy silently
 * drifted. One function used by both call sites can't drift like that again. Only MOBI_TRACK
 * (Track Point) lands on /tracking; every other authenticated role lands on the Dashboard.
 */
export const landingPathForRole = (role) => (role === ROLES.MOBI_TRACK ? "/tracking" : "/home");
export const isMobiTrack   = () => getRole() === ROLES.MOBI_TRACK;

/**
 * True only for a Track Point (MOBI_TRACK) driver whose account has no real password yet —
 * mirrors the Android app's SessionManager.getPasswordSet()/User.passwordSet. Read directly off
 * the stored login response (the same object LandingPage.applyLoginSession persists), not a
 * separate localStorage key, so there's only one place this can ever get out of sync with what
 * the backend actually returned. Used by App.js's PrivateRoute to redirect to
 * /set-initial-password on every protected route until it's resolved, and by
 * SetInitialPassword.js to clear itself back to `false` once the driver sets one.
 */
export const needsInitialPassword = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(LOGGED_IN_USER_KEY) || "null");
    return stored?.passwordSet === false;
  } catch {
    return false;
  }
};
